import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Card,
    Col,
    Empty,
    Input,
    InputNumber,
    Row,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import {
    BulbOutlined,
    InfoCircleOutlined,
    RocketOutlined,
    SearchOutlined,
    SwapOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface AdviceAIConfigRow {
    id?: number;
    expert_type: string;
    advice_prompt: string;
    /** Optional role/persona prepended to every advice call. Falls back to a hardcoded default if null. */
    system_prompt?: string | null;
    target_word_count?: number;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface AdviceAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        advice_ai_config?: AdviceAIConfigRow[];
    };
}

interface ExpertMeta {
    name: string;
    role: string;
    icon: React.ReactNode;
    color: string;
    description: string;
}

const ADVICE_EXPERT_META: Record<string, ExpertMeta> = {
    advice_key_findings: {
        name: 'Key Findings',
        role: 'Brand Strategist',
        icon: <BulbOutlined />,
        color: '#6B46C1',
        description:
            'Overall performance summary, strengths/weaknesses, and score interpretation.',
    },
    advice_explore_further: {
        name: 'Explore Further',
        role: 'Research Analyst',
        icon: <SearchOutlined />,
        color: '#3b82f6',
        description:
            'Deep-dive into category patterns, statement-level insights, and cross-category relationships.',
    },
    advice_opportunities: {
        name: 'Opportunities',
        role: 'Growth Strategist',
        icon: <RocketOutlined />,
        color: '#10b981',
        description:
            'Data-driven opportunities from actual scores; specific, actionable recommendations.',
    },
    advice_threats: {
        name: 'Threats',
        role: 'Risk Analyst',
        icon: <WarningOutlined />,
        color: '#ef4444',
        description:
            'Data-driven threats from weak scores and competitive vulnerabilities.',
    },
    advice_notable_changes: {
        name: 'Notable Changes',
        role: 'Trend Analyst',
        icon: <SwapOutlined />,
        color: '#f97316',
        description:
            'Temporal patterns, data recency, response volume trends. Uses tools to fetch multi-period brand-equity data — its prompt is appended with a fixed instruction at runtime.',
    },
};

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for Advice AI.
 *
 * Vertical sidebar tabs — one tab per advice_ai_config row, labelled with the
 * persona's friendly name + role. Provider/fallback and public-page settings
 * (beta banner, teaser word count) live above the tabs because they apply
 * across the whole subsystem.
 */
export const AdviceAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<AdviceAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.advice_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const updateRow = (expertType: string, patch: Partial<AdviceAIConfigRow>) => {
        editor.setPayload((prev) => {
            const next = (prev!.configTableRows.advice_ai_config ?? []).map((r) =>
                r.expert_type === expertType ? { ...r, ...patch } : r,
            );
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, advice_ai_config: next },
            };
        });
    };

    const primaryValue: ProviderModelTempValue = {
        provider: (settings.ADVICE_AI_PRIMARY_PROVIDER as ProviderId) || guessProvider(settings.ADVICE_AI_PRIMARY_MODEL),
        model: settings.ADVICE_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.ADVICE_AI_PRIMARY_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.ADVICE_AI_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.ADVICE_AI_FALLBACK_MODEL),
        model: settings.ADVICE_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.ADVICE_AI_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            ADVICE_AI_PRIMARY_PROVIDER: next.provider,
            ADVICE_AI_PRIMARY_MODEL: next.model,
            ADVICE_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            ADVICE_AI_FALLBACK_PROVIDER: next.provider,
            ADVICE_AI_FALLBACK_MODEL: next.model,
            ADVICE_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    const betaMode = (settings.ADVICE_BETA_MODE ?? 'false').toString().toLowerCase() === 'true';
    const betaMessage = settings.ADVICE_BETA_MESSAGE ?? '';
    const teaserWordCount = Number(settings.ADVICE_TEASER_WORD_COUNT ?? 0);

    const activeCount = rows.filter((r) => isActiveBool(r.is_active)).length;

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Five-section structured brand advice (key findings, explore further,
                    opportunities, threats, notable changes).{' '}
                    <strong>{activeCount}</strong> of <strong>{rows.length}</strong> sections
                    active.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card title="Public-page settings (beta banner + teaser)" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={6}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Beta banner</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>ADVICE_BETA_MODE</code>
                            </Text>
                            <Switch
                                checked={betaMode}
                                onChange={(checked) => setSetting('ADVICE_BETA_MODE', checked ? 'true' : 'false')}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} md={12}>
                        <Text strong>Beta banner message</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>ADVICE_BETA_MESSAGE</code>
                        </Text>
                        <Input.TextArea
                            rows={2}
                            value={betaMessage}
                            onChange={(e) => setSetting('ADVICE_BETA_MESSAGE', e.target.value)}
                            placeholder="(empty)"
                            style={{ marginTop: 4 }}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <Space direction="vertical" size={2}>
                            <Text strong>Teaser word count</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>ADVICE_TEASER_WORD_COUNT</code> — words shown before paywall.
                            </Text>
                            <InputNumber
                                min={0}
                                step={5}
                                value={teaserWordCount}
                                onChange={(v) => setSetting('ADVICE_TEASER_WORD_COUNT', String(v ?? 0))}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            {rows.length === 0 ? (
                <Empty description="No advice_ai_config rows — has the v1 baseline been seeded?" />
            ) : (
                <Card>
                    <Tabs
                        tabPosition="left"
                        style={{ minHeight: 600 }}
                        items={rows.map((row) => {
                            const meta = ADVICE_EXPERT_META[row.expert_type];
                            const isActive = isActiveBool(row.is_active);
                            return {
                                key: row.expert_type,
                                label: (
                                    <span>
                                        {isActive ? (
                                            <Badge dot status="success" offset={[-4, 0]}>
                                                <span>
                                                    <span style={{ color: meta?.color, marginRight: 8 }}>
                                                        {meta?.icon}
                                                    </span>
                                                    {meta?.name ?? row.expert_type}
                                                </span>
                                            </Badge>
                                        ) : (
                                            <span>
                                                <span style={{ color: meta?.color, marginRight: 8, opacity: 0.5 }}>
                                                    {meta?.icon}
                                                </span>
                                                <span style={{ opacity: 0.6 }}>
                                                    {meta?.name ?? row.expert_type}
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                ),
                                children: (
                                    <AdviceRowEditor
                                        row={row}
                                        meta={meta}
                                        onChange={(patch) => updateRow(row.expert_type, patch)}
                                    />
                                ),
                            };
                        })}
                    />
                </Card>
            )}
        </div>
    );
};

interface RowEditorProps {
    row: AdviceAIConfigRow;
    meta: ExpertMeta | undefined;
    onChange: (patch: Partial<AdviceAIConfigRow>) => void;
}

const AdviceRowEditor: React.FC<RowEditorProps> = ({ row, meta, onChange }) => {
    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message={
                    <span>
                        <strong>{meta?.name ?? row.expert_type}</strong>
                        {meta?.role && (
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                · {meta.role}
                            </Text>
                        )}
                    </span>
                }
                description={meta?.description ?? `expert_type: ${row.expert_type}`}
            />

            <Space style={{ marginBottom: 16 }} wrap size={16}>
                <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                    {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                </Tag>
                <Switch
                    checked={isActiveBool(row.is_active)}
                    onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                    checkedChildren="on"
                    unCheckedChildren="off"
                />
                <span>
                    <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>
                        target words
                    </Text>
                    <InputNumber
                        min={0}
                        step={10}
                        value={Number(row.target_word_count ?? 200)}
                        onChange={(v) => onChange({ target_word_count: Number(v ?? 200) })}
                    />
                </span>
            </Space>

            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                <code>system_prompt</code> sets the role/persona,{' '}
                <code>advice_prompt</code> is the per-section user-message template. The brand
                equity data context (overall score, category performance, statement-level
                metrics) is appended to <code>advice_prompt</code> at runtime by{' '}
                <code>AdviceAIService.buildAdvicePrompt</code> — there are no{' '}
                <code>{'{token}'}</code> substitutions for it.
            </Paragraph>

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="advice_ai"
                    label="System / persona prompt — system_prompt"
                    value={row.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                    placeholder="(empty — falls back to a hardcoded default in AdviceAiService)"
                />
            </div>
            <PromptEditor
                consumerSubsystem="advice_ai"
                label="Advice / user-message template — advice_prompt"
                value={row.advice_prompt ?? ''}
                onChange={(v) => onChange({ advice_prompt: v })}
                rows={14}
            />
        </div>
    );
};
